import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, TablePagination, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EmailTemplateFilters from "../../../components/Admin/EmailTemplates/EmailTemplateFilters";
import EmailTemplateTable from "../../../components/Admin/EmailTemplates/EmailTemplateTable";
import EmailTemplateForm from "../../../components/Admin/EmailTemplates/EmailTemplateForm";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useListEmailTemplate, useDeleteEmailTemplate, getListEmailTemplateQueryKey, downloadEmailTemplate } from "../../../api/bpm/endpoints";
import { customInstanceBpm } from "../../../api/axios-client";
import { useAuth } from "../../../auth";
import { env } from "../../../utils/env";

const emailTemplatesSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const buildEmailTemplateFormData = (input: { templateName: string; subjectTemplate?: string }, file?: File) => {
  const formData = new FormData();
  formData.append("templateName", input.templateName);

  if (input.subjectTemplate) {
    formData.append("subjectTemplate", input.subjectTemplate);
  }

  if (file) {
    formData.append("htmlFile", file);
  }

  return formData;
};

const createEmailTemplateWithFile = (tenant: string, input: { templateName: string; subjectTemplate?: string }, file: File) =>
  customInstanceBpm(`/v1/${tenant}/email-templates/form`, {
    method: "POST",
    body: buildEmailTemplateFormData(input, file),
  });

const updateEmailTemplateWithFile = (tenant: string, templateId: string, input: { templateName: string; subjectTemplate?: string }, file?: File) =>
  customInstanceBpm(`/v1/${tenant}/email-templates/form/${templateId}`, {
    method: "PUT",
    body: buildEmailTemplateFormData(input, file),
  });

export const Route = createFileRoute('/__auth/admin/email-templates')({
  component: EmailTemplatesComponent,
  validateSearch: (search) => emailTemplatesSearchSchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin && !context.auth.hasPermission("emailtemplate.view")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  staticData: {
    title: "emailTemplates.title",
  },
});

function EmailTemplatesComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant, hasPermission, user } = useAuth();
  const canCreate = Boolean(user?.isAdmin || hasPermission("emailtemplate.new"));
  const canEdit = Boolean(user?.isAdmin || hasPermission("emailtemplate.edit"));
  const canDelete = Boolean(user?.isAdmin || hasPermission("emailtemplate.delete"));
  const canDownload = Boolean(user?.isAdmin || hasPermission("emailtemplate.download"));

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = currentTenant || env.VITE_APP_DEFAULT_TENANT || "default-domain";
  const emailTemplatesQueryKey = getListEmailTemplateQueryKey(effectiveTenant);

  const refreshEmailTemplates = () =>
    queryClient.invalidateQueries({
      queryKey: emailTemplatesQueryKey,
    });

  const { data, isLoading, error } = useListEmailTemplate(effectiveTenant, {
    query: {
      staleTime: 30000,
      select: (res: any) => {
        if (Array.isArray(res)) return res;
        if (res && Array.isArray(res.data)) return res.data;
        if (res && Array.isArray(res.content)) return res.content;
        return [];
      }
    }
  });

  const rows: any[] = useMemo(() => {
    let list = (data as any) || [];
    if (search.q) {
      list = list.filter((item: any) =>
        item.templateName?.toLowerCase().includes(search.q!.toLowerCase()) ||
        item.subjectTemplate?.toLowerCase().includes(search.q!.toLowerCase())
      );
    }
    return list;
  }, [data, search.q]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1
      }),
    });
  };

  const createMutation = useMutation({
    mutationFn: ({ input, file }: { input: { templateName: string; subjectTemplate?: string }; file: File }) =>
      createEmailTemplateWithFile(effectiveTenant, input, file),
  });
  const updateMutation = useMutation({
    mutationFn: ({ templateId, input, file }: { templateId: string; input: { templateName: string; subjectTemplate?: string }; file?: File }) =>
      updateEmailTemplateWithFile(effectiveTenant, templateId, input, file),
  });
  const deleteMutation = useDeleteEmailTemplate();

  const handleSave = async (values: any, file?: File) => {
    try {
      const input = {
        templateName: values.name,
        subjectTemplate: values.subject,
      };

      if (selectedTemplate?.id) {
        await updateMutation.mutateAsync({
          templateId: String(selectedTemplate.id),
          input,
          file,
        });
      } else {
        if (!file) {
          showSnackbar(t("admin.emailTemplate.htmlRequired"), "error");
          return;
        }

        await createMutation.mutateAsync({
          input,
          file,
        });
      }
      refreshEmailTemplates();
      showSnackbar(t("emailTemplates.saveSuccess"));
      setIsDialogOpen(false);
    } catch (err: any) {
      let errorMessage = t("emailTemplates.saveError");
      
      if (err.response?.data?.message) {
        try {
          const parsedMessage = JSON.parse(err.response.data.message);
          
          if (parsedMessage.status === 409 || (parsedMessage.message && parsedMessage.message.includes("already exists"))) {
            errorMessage = t("emailTemplates.nameConflict");
          } else {
            errorMessage = parsedMessage.message || errorMessage;
          }
        } catch (e) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showSnackbar(errorMessage, "error");
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync({ tenant: effectiveTenant, templateId: id });
    refreshEmailTemplates();
    showSnackbar(t("emailTemplates.deleteSuccess"));
  };

  const handleDownload = async (template: any) => {
    try {
      const response = await downloadEmailTemplate(effectiveTenant, template.id, { responseType: "blob" } as any);
      const blob = response as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${template.templateName || "template"}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar(t("admin.emailTemplate.downloadError"), "error");
    }
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateToDelete, setTemplateToDelete] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const paginatedData = useMemo(() => {
    const start = ((search.page || 1) - 1) * (search.size || 10);
    const end = start + (search.size || 10);
    return rows.slice(start, end);
  }, [rows, search.page, search.size]);

  return (
    <ListLayout
      title={t("emailTemplates.title")}
      subtitle={t("emailTemplates.subtitle")}
      filters={
        <EmailTemplateFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={canCreate ? (
        <IconButton color="primary" onClick={() => { setSelectedTemplate(null); setIsDialogOpen(true); }}>
          <AddIcon />
        </IconButton>
      ) : undefined}
    >
      <EmailTemplateTable
        data={paginatedData}
        isLoading={isLoading}
        onEdit={(item) => { setSelectedTemplate(item); setIsDialogOpen(true); }}
        onDelete={(item) => { setTemplateToDelete(item); setIsDeleteDialogOpen(true); }}
        onDownload={handleDownload}
        canEdit={canEdit}
        canDelete={canDelete}
        canDownload={canDownload}
      />

      <TablePagination
        component="div"
        count={rows.length}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("common.of")} ${count}`}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <EmailTemplateForm
            initialValues={selectedTemplate || undefined}
            onSubmit={handleSave}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={createMutation.isPending || updateMutation.isPending}
            canDownload={canDownload}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t("emailTemplates.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("emailTemplates.deleteConfirm", { name: templateToDelete?.templateName })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (templateToDelete?.id) {
                handleDelete(templateToDelete.id);
              }
              setIsDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </ListLayout>
  );
}
